/**
 * Team-Insights API — policy §5 conformance pass (2026-05-25).
 *
 * Three read-only endpoints that back the new insight panels on
 * `/manager`, `/hod/overview` and `/hr/insights`:
 *
 *   - pattern-signals  (manager+) — risk-band distribution + most-common pattern
 *   - sick-clustering  (HOD+)     — same-day same-team sick clusters
 *   - pending-backlog  (HOD+)     — pending leaves > N working days, by manager
 */
import { apiRequest } from './client'

// ── pattern-signals ─────────────────────────────────────────────────────────

export interface PatternSignalsResponse {
  high: number
  medium: number
  low: number
  totalEmployees: number
  mostCommonPattern: string | null
  mostCommonPatternCount: number
  scopeLabel: string
}

export function getPatternSignals(): Promise<PatternSignalsResponse> {
  return apiRequest<PatternSignalsResponse>('/api/v1/insights/pattern-signals')
}

// ── sick-clustering ─────────────────────────────────────────────────────────

export interface SickClusterDay {
  teamId: string
  teamName: string
  date: string // ISO date
  employeeCount: number
  // employeeNames intentionally removed — de-identified for Malaysia medical
  // confidentiality. Render counts only.
}

export interface SickClusteringResponse {
  windowDays: number
  threshold: number
  scopeLabel: string
  clusters: SickClusterDay[]
}

export interface SickClusteringQuery {
  windowDays?: number
  threshold?: number
}

export function getSickClustering(
  query: SickClusteringQuery = {},
): Promise<SickClusteringResponse> {
  const params = new URLSearchParams()
  if (query.windowDays != null) params.set('windowDays', String(query.windowDays))
  if (query.threshold != null) params.set('threshold', String(query.threshold))
  const qs = params.toString()
  return apiRequest<SickClusteringResponse>(
    `/api/v1/insights/sick-clustering${qs ? `?${qs}` : ''}`,
  )
}

// ── pending-backlog ─────────────────────────────────────────────────────────

export interface PendingBacklogManager {
  managerId: string
  managerName: string
  teamName: string | null
  pendingCount: number
  oldestAgeDays: number
}

export interface PendingBacklogResponse {
  minBusinessDays: number
  thresholdUnactioned: number
  managers: PendingBacklogManager[]
}

export interface PendingBacklogQuery {
  minBusinessDays?: number
  thresholdUnactioned?: number
}

export function getPendingBacklog(
  query: PendingBacklogQuery = {},
): Promise<PendingBacklogResponse> {
  const params = new URLSearchParams()
  if (query.minBusinessDays != null) {
    params.set('minBusinessDays', String(query.minBusinessDays))
  }
  if (query.thresholdUnactioned != null) {
    params.set('thresholdUnactioned', String(query.thresholdUnactioned))
  }
  const qs = params.toString()
  return apiRequest<PendingBacklogResponse>(
    `/api/v1/insights/pending-backlog${qs ? `?${qs}` : ''}`,
  )
}
